import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, Box, Typography, Button, IconButton, Chip, CircularProgress, Alert } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ShoppingCartRoundedIcon from '@mui/icons-material/ShoppingCartRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import { useNexusBilling } from '../context/NexusBillingContext';
import { formatPaise, FEATURE_META } from '../api/billingApi';

/**
 * "You've run out" modal. Opened by any feature with the backend's structured
 * 402 payload (AI_TUTOR_QUOTA_EXHAUSTED / MOCK_INTERVIEW_CREDITS_EXHAUSTED /
 * ELITE_MASTERY_PURCHASE_REQUIRED). Shows the matching product with the
 * CURRENT server price and lets the builder add it to the cart and go to
 * checkout in one step. Never surprises: the copy names the exact allowance
 * that ran out and what the purchase gives.
 */
const NexusQuotaExhaustedModal = () => {
  const navigate = useNavigate();
  const { quotaModal, closeQuotaModal, catalog, addToCart, refreshCatalog } = useNexusBilling();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const feature = quotaModal ? quotaModal.feature : null;
  const meta = feature ? FEATURE_META[feature] : null;
  const catFeature = useMemo(() => (catalog && feature ? catalog.features.find((f) => f.code === feature) : null), [catalog, feature]);
  const product = catFeature && catFeature.products ? catFeature.products[0] : null;
  const technology = quotaModal ? quotaModal.technology : null;
  const techTitle = technology && catalog ? (catalog.technologies.find((t) => t.slug === technology) || {}).title || technology : null;
  const quote = product ? (product.requiresTechnology && technology ? product.quotesByTechnology?.[technology] : product.quote) : null;

  if (!quotaModal) return null;

  const freePagesTotal = quotaModal.freePagesTotal != null ? quotaModal.freePagesTotal : null;
  const headline = feature === 'AI_TUTOR'
    ? `You've used all ${catFeature ? catFeature.freeQuota : ''} free ${quotaModal.subFeature ? quotaModal.subFeature.toLowerCase().replace(/_/g, ' ') : 'AI Tutor'} prompts for this month.`
    : feature === 'MOCK_INTERVIEW'
      ? `You've used your ${catFeature ? catFeature.freeQuota : ''} free interviews for this month.`
      : quotaModal.premiumExpired
        ? `Your full access to ${techTitle || 'this technology'} ended on ${new Date(quotaModal.premiumExpired.expiresAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}.`
        : `You've completed the ${freePagesTotal != null ? `${freePagesTotal} ` : ''}free ${techTitle || ''} pages.`;

  const display = catFeature && catFeature.display ? catFeature.display : {};
  const isFullAccess = !!(product && product.accessType === 'FULL_ACCESS');
  const cta = display.ctaLabel || (feature === 'AI_TUTOR' ? 'Recharge AI Tutor' : feature === 'MOCK_INTERVIEW' ? 'Get Interviews' : isFullAccess ? 'Unlock full access' : `Unlock ${product ? product.unitsPerPurchase : ''} Pages`);

  const buyNow = async () => {
    if (!product) return navigate('/nextgen-builder/plans');
    setBusy(true); setErr('');
    try {
      await addToCart({ productCode: product.code, technologySlug: product.requiresTechnology ? technology : null, quantity: 1 });
      closeQuotaModal();
      navigate('/nextgen-builder/checkout');
    } catch (e) {
      if (e.status === 401) { closeQuotaModal(); navigate('/nextgen-builder/login', { state: { from: window.location.pathname, reason: 'billing' } }); return; }
      setErr(e.message || 'Could not add to cart');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onClose={closeQuotaModal} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 4, p: 0.5 } }}>
      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Chip icon={<BoltRoundedIcon />} label={meta ? meta.label : 'TSG Nexus'} size="small" sx={{ bgcolor: 'rgba(108,59,255,0.08)', color: '#6C3BFF', fontWeight: 700 }} />
          <IconButton size="small" onClick={closeQuotaModal} aria-label="Close"><CloseRoundedIcon fontSize="small" /></IconButton>
        </Box>
        <Typography variant="h6" sx={{ mt: 2, fontWeight: 800, color: '#111827', lineHeight: 1.3 }}>{headline}</Typography>
        <Typography variant="body2" sx={{ mt: 1, color: '#6B7280' }}>
          {display.premiumDescription
            || (feature === 'ELITE_MASTERY'
              ? (isFullAccess
                ? `Unlock every page of ${techTitle || 'this technology'}${product.validity && product.validity.label ? ` for ${product.validity.label}` : ''} — new pages included. Each purchase is for one technology only.`
                : 'Paid pages are unlocked once and stay readable. Each purchase is for one technology only.')
              : feature === 'AI_TUTOR'
                ? 'Purchased credits are shared across all five AI Tutor tools and never expire at month end.'
                : 'Purchased interviews never expire at month end.')}
        </Typography>

        {!catalog && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}><CircularProgress size={16} /><Typography variant="caption">Loading prices…</Typography><Button size="small" onClick={refreshCatalog}>Retry</Button></Box>
        )}

        {product && (
          <Box sx={{ mt: 2.5, p: 2, borderRadius: 3, border: '1px solid #E5E7EB', bgcolor: '#F8FAFC' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#111827' }}>
              {product.name}{techTitle ? ` — ${techTitle}` : ''}
            </Typography>
            <Typography variant="body2" sx={{ color: '#374151', mt: 0.5 }}>
              {isFullAccess
                ? `Full access${product.validity && product.validity.label ? ` · ${product.validity.label}` : ''} · one technology`
                : `${product.unitsPerPurchase} additional ${meta ? meta.units : 'units'}`}
            </Typography>
            {quote && !quote.unavailable ? (
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 900, color: '#6C3BFF' }}>{formatPaise(quote.unitPricePaise)}</Typography>
                <Typography variant="caption" sx={{ color: '#6B7280' }}>+ {catalog.taxRate.percent}% GST</Typography>
              </Box>
            ) : quote && quote.unavailable ? (
              <Alert severity="warning" sx={{ mt: 1 }}>{quote.reason}</Alert>
            ) : null}
          </Box>
        )}

        {(quotaModal.remainingPaid > 0 || quotaModal.paidPagesRemaining > 0 || quotaModal.paidRemaining > 0) && (
          <Alert severity="info" sx={{ mt: 2 }}>You still have paid credits — please retry.</Alert>
        )}
        {err && <Alert severity="error" sx={{ mt: 2 }}>{err}</Alert>}

        <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
          <Button fullWidth variant="outlined" onClick={() => { closeQuotaModal(); navigate('/nextgen-builder/plans'); }} sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 700 }}>
            View plans
          </Button>
          <Button fullWidth variant="contained" disabled={busy || !product} onClick={buyNow} startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <ShoppingCartRoundedIcon />}
            sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 800, background: 'linear-gradient(135deg,#6C3BFF 0%,#8B5CF6 100%)', boxShadow: 'none' }}>
            {cta}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default NexusQuotaExhaustedModal;
